import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { useProviderAuth } from '@hooks/useProviderAuth'
import { useLocationEvidenceLogger } from '@hooks/useLocationEvidenceLogger'

interface ProtectedRouteProps {
  children: React.ReactNode
  role?: 'patient' | 'provider'
}

function PatientLocationEvidence() {
  const { patient } = usePatientAuth()

  useLocationEvidenceLogger({
    role: 'patient',
    actorId: patient?.id,
  })

  return null
}

function ProviderLocationEvidence() {
  const { provider } = useProviderAuth()

  useLocationEvidenceLogger({
    role: 'provider',
    actorId: provider?.id,
  })

  return null
}

export default function ProtectedRoute({ children, role }: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading, role: userRole } = useAuth()
  const { isLoading: isProviderLoading } = useProviderAuth()
  const authRedirectPath = role ? `/auth/login?role=${role}` : '/auth/login'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={authRedirectPath} replace state={{ from: location }} />
  }

  if (role && userRole !== role) {
    return <Navigate to={authRedirectPath} replace state={{ from: location }} />
  }

  if (role === 'provider' && isProviderLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <>
      {role === 'patient' ? <PatientLocationEvidence /> : null}
      {role === 'provider' ? <ProviderLocationEvidence /> : null}
      {children}
    </>
  )
}
